import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";
import { Button, Card, DataTable, EstadoVazio, Pill, fmtBRL, type Column } from "@atlas/ui/web";
import { ApiHttpError, type DetalheBeneficio, type SituacaoBeneficio } from "@atlas/sdk";
import { atlas } from "../../../lib/sdk";
import { mensagemDeErro } from "../../../lib/erro-validacao";
import { bancoPermissaoCobre, readBancoPermissoesDaSessao } from "../../../lib/banco-perms";
import { EncerrarBeneficioModal } from "./encerrar-modal";
import { AlterarValorBeneficioModal } from "./alterar-valor-modal";
import {
  OCUPA_MARGEM,
  ROTULO_COMPORTAMENTO,
  ROTULO_OPERACAO,
  ROTULO_ORIGEM_VALOR,
  mesAno,
  situacaoDoBeneficio,
} from "../../../lib/beneficios";

// O DETALHE DE UM BENEFÍCIO (#316): os dados, se ocupa margem e por quê, e o
// histórico de valores e de operações. As ações ficam no cabeçalho: encerrar
// (#317) e alterar o valor (#320); o pedido do servidor é atendido pelo encerrar (#318).

const reais = (centavos: number) => fmtBRL(centavos / 100);

/** O que ainda desconta, ou vai descontar, pode ser encerrado; a inclusão pendente, retirada. */
const ENCERRAVEL: readonly SituacaoBeneficio[] = ["ativa", "encerrando", "aguardando_autorizacao"];
/** Só o que desconta tem valor a alterar; a inclusão pendente não tem ainda. */
const ALTERAVEL: readonly SituacaoBeneficio[] = ["ativa", "encerrando"];

type Valor = DetalheBeneficio["historico"]["valores"][number];
type Operacao = DetalheBeneficio["historico"]["operacoes"][number];

function Campo({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <span style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
        {rotulo}
      </span>
      <span>{children}</span>
    </div>
  );
}

const dataBr = (iso: string) => {
  const d = new Date(iso.length === 10 ? `${iso}T12:00:00` : iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

export function BancoBeneficioDetalhe() {
  const { id = "" } = useParams<{ id: string }>();
  const q = useQuery({
    queryKey: ["banco", "beneficios", "detalhe", id],
    queryFn: () => atlas.banco.beneficios.detalhe(id),
    retry: false,
  });
  const [encerrando, setEncerrando] = useState(false);
  const [alterando, setAlterando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);
  // As ações aparecem só para quem opera benefícios; a API confere de novo.
  const podeOperar = bancoPermissaoCobre(readBancoPermissoesDaSessao(), "beneficios-operar");

  const voltar = <Link to="/banco/beneficios">← Voltar aos benefícios</Link>;

  if (q.isPending) return <div style={{ color: "var(--text-muted)" }}>Carregando o benefício…</div>;
  if (q.isError) {
    // Inexistente e de outra consignatária chegam iguais: o mesmo 404.
    const naoAchou = q.error instanceof ApiHttpError && q.error.status === 404;
    return (
      <div style={{ display: "grid", gap: 12 }}>
        {naoAchou ? (
          <EstadoVazio titulo="Benefício não encontrado" orientacao="Ele pode ter mudado de consignatária, ou o endereço está errado." />
        ) : (
          <p role="alert">{mensagemDeErro(q.error, "Não foi possível carregar o benefício.")}</p>
        )}
        {voltar}
      </div>
    );
  }

  const { beneficio: b, historico } = q.data;
  const situacao = situacaoDoBeneficio(b.situacao, b.competenciaFim);
  const ocupa = OCUPA_MARGEM[b.ocupaMargem];

  const colunasValores: Column<Valor>[] = [
    { key: "inicio", header: "A partir de", mono: true, render: (v) => mesAno(v.competenciaInicio) },
    { key: "fim", header: "Até", mono: true, render: (v) => (v.competenciaFim ? mesAno(v.competenciaFim) : "—") },
    { key: "valor", header: "Valor", align: "right", render: (v) => (v.valorCentavos === 0 ? "Encerrado" : reais(v.valorCentavos)) },
    { key: "origem", header: "Origem", render: (v) => ROTULO_ORIGEM_VALOR[v.origem] ?? v.origem },
  ];
  const colunasOperacoes: Column<Operacao>[] = [
    { key: "quando", header: "Registrada em", render: (o) => dataBr(o.registradaEm) },
    { key: "tipo", header: "Operação", render: (o) => ROTULO_OPERACAO[o.tipo] },
    { key: "competencia", header: "Competência", mono: true, render: (o) => mesAno(o.competencia) },
    {
      key: "valores",
      header: "Valor",
      align: "right",
      render: (o) =>
        o.valorAntesCentavos == null ? reais(o.valorDepoisCentavos) : `${reais(o.valorAntesCentavos)} → ${reais(o.valorDepoisCentavos)}`,
    },
    { key: "autor", header: "Por", render: (o) => (o.autor === "averbadora" ? "Averbadora" : (o.operador ?? "Você")) },
    { key: "motivo", header: "Motivo", wrap: true, render: (o) => o.motivo },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {voltar}
      <header style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ margin: 0, fontSize: "1.6rem", letterSpacing: "-0.02em" }}>{b.rotulo}</h1>
        <Pill variant={situacao.variante}>{situacao.rotulo}</Pill>
        {podeOperar && ENCERRAVEL.includes(b.situacao) ? (
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            {ALTERAVEL.includes(b.situacao) ? (
              <Button variant="ghost" onClick={() => setAlterando(true)}>Alterar valor</Button>
            ) : null}
            <Button variant="ghost" onClick={() => setEncerrando(true)}>
              {b.situacao === "aguardando_autorizacao" ? "Retirar inclusão" : "Encerrar benefício"}
            </Button>
          </div>
        ) : null}
      </header>

      {aviso ? (
        <Card>
          <p role="status" style={{ margin: 0 }}>{aviso}</p>
        </Card>
      ) : null}

      <Card>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
          <Campo rotulo="Servidor">{b.nome ?? "—"}</Campo>
          <Campo rotulo="CPF">{b.cpfMasked}</Campo>
          <Campo rotulo="Matrícula">{`${b.matricula} · FF ${b.folha}`}</Campo>
          <Campo rotulo="Órgão/entidade">{b.orgao}</Campo>
          <Campo rotulo="Rubrica">{b.descricao ? `${b.rubrica} — ${b.descricao}` : b.rubrica}</Campo>
          <Campo rotulo="Credor final">{b.credorFinal?.nome ?? "—"}</Campo>
          <Campo rotulo="Valor">{reais(b.valorCentavos)}</Campo>
          <Campo rotulo="Desconto">{ROTULO_COMPORTAMENTO[b.comportamento] ?? b.comportamento}</Campo>
          <Campo rotulo="Vigência">
            {`desde ${mesAno(b.competenciaInicio)}${b.competenciaFim ? ` até ${mesAno(b.competenciaFim)}` : ""}`}
          </Campo>
        </div>
      </Card>

      <Card>
        <div style={{ display: "grid", gap: 6 }}>
          <span style={{ fontSize: 11, letterSpacing: "0.06em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Ocupa margem
          </span>
          <div>
            <Pill variant={ocupa.variante}>{ocupa.rotulo}</Pill>
          </div>
          <span style={{ color: "var(--text-muted)" }}>{ocupa.explicacao}</span>
        </div>
      </Card>

      {b.pedidoCancelamento || b.pedidoAlteracao || b.autorizacao ? (
        <Card>
          <div style={{ display: "grid", gap: 8 }}>
            {b.pedidoCancelamento ? (
              <span style={{ color: b.pedidoCancelamento.vencido ? "var(--danger-500)" : "var(--text)", fontWeight: 600 }}>
                O servidor pediu o cancelamento — responder até {dataBr(b.pedidoCancelamento.responderAte)}
                {b.pedidoCancelamento.vencido ? " (prazo vencido)" : ""}.{" "}
                <Link to="/banco/pedidos-cancelamento">Ver em Solicitações</Link>
              </span>
            ) : null}
            {b.pedidoAlteracao ? (
              <span style={{ color: b.pedidoAlteracao.vencido ? "var(--danger-500)" : "var(--text)", fontWeight: 600 }}>
                O servidor pediu uma alteração — responder até {dataBr(b.pedidoAlteracao.responderAte)}
                {b.pedidoAlteracao.vencido ? " (prazo vencido)" : ""}.{" "}
                <Link to="/banco/pedidos-cancelamento">Responder em Solicitações</Link>
              </span>
            ) : null}
            {b.autorizacao ? (
              <span>
                {b.autorizacao.tipo === "inclusao" ? "Inclusão" : "Aumento"} de {reais(b.autorizacao.valorCentavos)} aguardando a
                autorização do servidor, até {dataBr(b.autorizacao.prazoAte)}.
              </span>
            ) : null}
          </div>
        </Card>
      ) : null}

      {encerrando ? <EncerrarBeneficioModal beneficio={b} onFechar={() => setEncerrando(false)} onFeito={setAviso} /> : null}
      {alterando ? <AlterarValorBeneficioModal beneficio={b} onFechar={() => setAlterando(false)} onFeito={setAviso} /> : null}

      <Card>
        <h2 style={{ margin: "0 0 12px", fontSize: "1.1rem" }}>Histórico de valores</h2>
        <DataTable
          caption="Histórico de valores"
          columns={colunasValores}
          rows={historico.valores}
          rowKey={(v) => v.competenciaInicio}
          emptyState={<EstadoVazio titulo="Nenhum valor gravado ainda" />}
        />
      </Card>

      <Card>
        <h2 style={{ margin: "0 0 12px", fontSize: "1.1rem" }}>Operações</h2>
        <DataTable
          caption="Operações"
          columns={colunasOperacoes}
          rows={historico.operacoes}
          rowKey={(o) => `${o.registradaEm}-${o.tipo}`}
          emptyState={<EstadoVazio titulo="Nenhuma operação registrada" orientacao="Os valores vieram do arquivo mensal ou da carga." />}
        />
      </Card>
    </div>
  );
}
